/**
 * A porta entre o orçamento e planejamento (módulo 19; 0106) e o servidor: as
 * versões, as linhas, as premissas, a sugestão do histórico, o previsto ×
 * realizado, os cenários e o caixa. A mesa e o celular usam a mesma.
 */
import { comCorpo, pedir } from '@/lib/pedido';

export type Situacao = 'rascunho' | 'aprovado' | 'arquivado';
export type Natureza = 'receita' | 'custo' | 'despesa' | 'investimento';
export type Direcionador = 'horas' | 'litros' | 'fixo';
export type OrigemDaLinha = 'manual' | 'historico' | 'cenario' | 'proposta_de_investimento';
export type CodigoDaPremissa = 'preco_diesel' | 'utilizacao' | 'prazo_medio_recebimento' | 'prazo_medio_pagamento' | 'taxa_custo_de_capital';
export type GrupoDaConta = 'receita' | 'custo_direto' | 'custo_indireto' | 'depreciacao';

export interface Versao {
  readonly id: string;
  readonly exercicio: number;
  readonly versao: number;
  readonly nome: string;
  readonly situacao: Situacao;
  readonly substituiId: string | null;
  readonly substituiVersao: number | null;
  readonly montadoPorId: string;
  readonly montadoPor: string | null;
  readonly aprovadoPor: string | null;
  readonly aprovadoEm: string | null;
  readonly nota: string | null;
  readonly criadoEm: string;
  readonly arquivadoEm: string | null;
}

export interface VersaoNaLista extends Versao {
  readonly linhas: number;
  readonly receita: number;
  readonly custoEDespesa: number;
  readonly investimento: number;
}

export interface Versoes {
  readonly exercicioCorrente: number;
  readonly atual: string | null;
  readonly versoes: readonly VersaoNaLista[];
}

export interface ValorDoMes {
  readonly competencia: string;
  readonly quantidade: number | null;
  readonly preco: number | null;
  readonly valor: number;
}

export interface LinhaDoOrcamento {
  readonly id: string;
  readonly centroId: string;
  readonly centroCodigo: string;
  readonly centroNome: string;
  readonly contaId: string | null;
  readonly contaCodigo: string | null;
  readonly contaNome: string | null;
  readonly natureza: Natureza;
  readonly contratoId: string | null;
  readonly contratoCodigo: string | null;
  readonly equipamentoId: string | null;
  readonly equipamentoCodigo: string | null;
  readonly direcionador: Direcionador;
  readonly memoria: string;
  readonly origem: OrigemDaLinha;
  readonly propostaId: string | null;
  readonly gravadoPor: string | null;
  readonly valores: readonly ValorDoMes[];
  readonly total: number;
}

export interface TotaisDaNatureza {
  readonly receita: number;
  readonly custo: number;
  readonly despesa: number;
  readonly investimento: number;
  readonly resultado: number;
}

export interface PremissaDaVersao {
  readonly codigo: CodigoDaPremissa;
  readonly rotulo: string;
  readonly unidade: string;
  readonly ondeEntra: string;
  readonly valor: number | null;
  readonly fonte: string | null;
  readonly origem: 'historico' | 'informada' | null;
  readonly gravadoPor: string | null;
  readonly atualizadoEm: string | null;
  readonly sugestao: { readonly valor: number; readonly fonte: string } | null;
}

export interface DetalheDaVersao {
  readonly versao: Versao & { readonly podeAprovar: boolean; readonly temLicencaDeAprovar: boolean; readonly impedimentoDeAprovar: string | null };
  readonly competencias: readonly string[];
  readonly linhas: readonly LinhaDoOrcamento[];
  readonly totais: { readonly porNatureza: TotaisDaNatureza; readonly porMes: readonly (TotaisDaNatureza & { readonly competencia: string })[] };
  readonly premissas: readonly PremissaDaVersao[];
}

export interface Opcoes {
  readonly centros: readonly { readonly id: string; readonly codigo: string; readonly nome: string }[];
  readonly contas: readonly { readonly id: string; readonly codigo: string; readonly nome: string; readonly grupo: GrupoDaConta; readonly natureza: Exclude<Natureza, 'investimento'> }[];
  readonly contratos: readonly { readonly id: string; readonly codigo: string; readonly descricao: string }[];
  readonly equipamentos: readonly { readonly id: string; readonly codigo: string; readonly descricao: string }[];
  readonly tipos: readonly string[];
}

export interface LinhaParaGravar {
  readonly id: string | null;
  readonly centroCustoId: string;
  readonly contaId: string | null;
  readonly natureza: Natureza;
  readonly contratoId: string | null;
  readonly equipamentoId: string | null;
  readonly direcionador: Direcionador;
  readonly memoria: string;
  readonly valores: readonly { readonly competencia: string; readonly quantidade: number | null; readonly preco: number | null; readonly valor: number }[];
}

export interface Sugestao {
  readonly competencias: readonly string[];
  readonly sugestoes: readonly {
    readonly chave: string;
    readonly centro: { readonly id: string; readonly codigo: string; readonly nome: string };
    readonly conta: { readonly id: string; readonly codigo: string; readonly nome: string; readonly grupo: GrupoDaConta };
    readonly natureza: Natureza;
    readonly mediaMensal: number;
    readonly mesesComValor: number;
    readonly mesesNoHistorico: number;
    readonly memoria: string;
    readonly jaNoOrcamento: boolean;
  }[];
  readonly naoEntrou: readonly string[];
}

export interface Explicacao {
  readonly texto: string;
  readonly autor: string | null;
  readonly em: string;
}

export interface LinhaDoDesvio {
  readonly chave: string;
  readonly natureza: 'receita' | 'custo' | 'despesa' | null;
  readonly previsto: number;
  readonly comprometido: number;
  readonly realizado: number;
  readonly saldo: number;
  readonly desvio: number;
  readonly desvioPct: number | null;
  readonly sentido: 'melhor' | 'pior' | 'neutro';
  readonly relevante: boolean;
  readonly precisaExplicar: boolean;
  readonly explicacao: Explicacao | null;
  readonly decomposicao: {
    readonly quantidadePrevista: number;
    readonly quantidadeRealizada: number;
    readonly precoPrevisto: number;
    readonly precoRealizado: number;
    readonly efeitoVolume: number;
    readonly efeitoPreco: number;
  } | null;
  readonly acumulado: { readonly previsto: number; readonly realizado: number; readonly desvio: number; readonly desvioPct: number | null };
  readonly centro: { readonly id: string; readonly codigo: string; readonly nome: string } | null;
  readonly conta: { readonly id: string; readonly codigo: string | null; readonly nome: string; readonly grupo: GrupoDaConta | null };
  readonly origem: {
    readonly linhas: readonly { readonly id: string; readonly memoria: string; readonly valor: number }[];
    readonly pedidos: readonly { readonly numero: string; readonly fornecedor: string; readonly produto: string; readonly pendente: number; readonly valor: number; readonly entregaPrevista: string }[];
  };
}

export interface CentroDoDesvio {
  readonly centro: { readonly id: string; readonly codigo: string; readonly nome: string } | null;
  readonly receita: { readonly previsto: number; readonly realizado: number };
  readonly gasto: { readonly previsto: number; readonly comprometido: number; readonly realizado: number; readonly saldo: number };
  readonly relevantes: number;
  readonly semExplicacao: number;
}

export interface Premissas {
  readonly inclui: readonly string[];
  readonly naoInclui: readonly string[];
  readonly completo: boolean;
}

export interface PrevistoRealizado {
  readonly versao: { readonly id: string; readonly exercicio: number; readonly versao: number; readonly nome: string; readonly situacao: Situacao } | null;
  readonly competencia: string;
  readonly emAndamento: boolean;
  readonly limiares: { readonly pct: number; readonly valor: number; readonly padrao: boolean };
  readonly linhas: readonly LinhaDoDesvio[];
  readonly porCentro: readonly CentroDoDesvio[];
  readonly totais: {
    readonly receita: { readonly previsto: number; readonly realizado: number };
    readonly gasto: { readonly previsto: number; readonly comprometido: number; readonly realizado: number; readonly saldo: number };
  };
  readonly resumo: { readonly relevantes: number; readonly semExplicacao: number };
  readonly premissas: Premissas;
}

export interface TotaisDoCenario {
  readonly receita: number;
  readonly combustivel: number;
  readonly manutencao: number;
  readonly fixos: number;
  readonly resultado: number;
}

export interface Hipotese {
  readonly tipo: string;
  readonly contratoId: string | null;
  readonly contratoCodigo: string | null;
  readonly horasMes: number;
  readonly fonte: string;
}

export interface Cenario {
  readonly id: string;
  readonly nome: string;
  readonly utilizacaoPct: number;
  readonly precoDiesel: number | null;
  readonly valorHoraPct: number;
  readonly manutencaoPct: number;
  readonly observacao: string;
  readonly gravadoPor: string | null;
  readonly atualizadoEm: string;
  /** A versao do cenario (backend/src/http/versao-da-ficha.ts): o formulario a devolve no `PUT`. */
  readonly versao?: string | null;
  readonly hipoteses: readonly Hipotese[];
}

export interface Cenarios {
  readonly frase: string;
  readonly avisoDeCenarioUnico: string | null;
  readonly versao: { readonly id: string; readonly exercicio: number; readonly versao: number; readonly nome: string; readonly situacao: Situacao };
  readonly base: {
    readonly competencias: readonly string[];
    readonly demanda: readonly { readonly tipo: string; readonly contratoId: string | null; readonly contratoCodigo: string | null; readonly horasMes: number; readonly receitaMes: number; readonly valorHora: number | null }[];
    readonly custoPorTipo: readonly { readonly tipo: string; readonly combustivelPorHora: number | null; readonly manutencaoPorHora: number | null }[];
    readonly precoDieselDaPremissa: number | null;
    readonly naoInclui: readonly string[];
  };
  readonly orcado: TotaisDoCenario;
  readonly cenarios: readonly (Cenario & { readonly total: TotaisDoCenario; readonly completo: boolean })[];
}

export interface Simulacao {
  readonly frase: string;
  readonly cenario: Cenario;
  readonly resultado: {
    readonly porTipo: readonly { readonly tipo: string; readonly contratoId: string | null; readonly contratoCodigo: string | null; readonly origem: 'historico' | 'hipotese'; readonly horasMes: number; readonly receitaMes: number | null; readonly combustivelMes: number | null; readonly manutencaoMes: number | null }[];
    readonly mensal: readonly (TotaisDoCenario & { readonly competencia: string })[];
    readonly total: TotaisDoCenario;
  };
  readonly sensibilidade: readonly { readonly variavel: string; readonly rotulo: string; readonly menos10: number; readonly mais10: number; readonly deltaMenos10: number; readonly deltaMais10: number }[];
  readonly orcado: TotaisDoCenario;
  readonly precoDieselDaPremissa: number | null;
  readonly premissas: Premissas;
}

export type OrigemDoEvento = 'documento' | 'medicao' | 'receita_orcada' | 'fatura_de_posto' | 'pedido' | 'titulo' | 'titulo_a_receber' | 'custo_orcado' | 'investimento';

export interface Caixa {
  readonly dataBase: string;
  readonly fim: string;
  readonly saldoInicial: number;
  readonly saldoInformado: boolean;
  readonly eventos: readonly { readonly data: string; readonly tipo: 'entrada' | 'saida'; readonly origem: OrigemDoEvento; readonly referencia: string; readonly valor: number; readonly atrasado: boolean; readonly saldoDepois: number }[];
  readonly menor: { readonly valor: number; readonly data: string };
  readonly porMes: readonly { readonly competencia: string; readonly entradas: number; readonly saidas: number; readonly saldoFinal: number }[];
  readonly totais: { readonly entradas: number; readonly saidas: number };
  readonly deduplicados: {
    readonly medicoesComDocumento: number; readonly pedidoAbatidoPorTitulo: number; readonly titulosJaPagos: number; readonly custoOrcadoCoberto: number;
    /* 0108: o título da matriz do módulo 04 representa o recebimento, a cobrança e a fatura do posto. */
    readonly recebimentoAbatidoPeloErp?: number; readonly cobrancaNoTituloDoErp?: number; readonly faturaDePostoNoTituloDoErp?: number;
  };
  readonly naoEntrou: readonly string[];
  readonly completo: boolean;
  readonly horizonte: number;
  readonly versao: { readonly id: string; readonly exercicio: number; readonly versao: number; readonly nome: string } | null;
  readonly saldos: readonly { readonly id: string; readonly data: string; readonly valor: number; readonly fonte: string; readonly informadoPor: string | null; readonly criadoEm: string }[];
  readonly frase: string;
}

export interface VariaveisDoCenario {
  readonly nome: string;
  readonly utilizacaoPct: number;
  readonly precoDiesel: number | null;
  readonly valorHoraPct: number;
  readonly manutencaoPct: number;
  readonly observacao?: string;
}

const id = (v: string) => encodeURIComponent(v);

export const apiDoOrcamento = {
  versoes: () => pedir<Versoes>('/orcamento/versoes'),
  versao: (v: string) => pedir<DetalheDaVersao>(`/orcamento/versoes/${id(v)}`),
  opcoes: () => pedir<Opcoes>('/orcamento/opcoes'),
  criarVersao: (exercicio: number, nome: string) =>
    comCorpo<{ id: string; exercicio: number; versao: number; nome: string }>('POST', '/orcamento/versoes', { exercicio, nome: nome.trim() }),
  revisar: (v: string) => comCorpo<{ id: string; exercicio: number; versao: number; nome: string; substitui: number; linhas: number }>('POST', `/orcamento/versoes/${id(v)}/revisao`, {}),
  aprovar: (v: string, nota: string) =>
    comCorpo<{ id: string; exercicio: number; versao: number; nome: string; arquivada: number | null; receita: number; resultado: number; aprovadoPor: string | null }>('POST', `/orcamento/versoes/${id(v)}/aprovacao`, { nota: nota.trim() }),
  descartar: (v: string, motivo: string) => comCorpo<{ id: string; exercicio: number; versao: number; nome: string }>('POST', `/orcamento/versoes/${id(v)}/descarte`, { motivo: motivo.trim() }),
  gravarLinhas: (v: string, linhas: readonly LinhaParaGravar[]) =>
    pedir<{ versao: number; exercicio: number; gravadas: number; linhas: { id: string; memoria: string; total: number }[]; total: number }>(`/orcamento/versoes/${id(v)}/linhas`, {
      method: 'PUT',
      body: JSON.stringify({ linhas: linhas.map((l) => ({ ...l, memoria: l.memoria.trim() })) }),
    }),
  removerLinha: (v: string, linha: string) =>
    comCorpo<{ id: string; memoria: string; centro: string; conta: string | null; total: number }>('POST', `/orcamento/versoes/${id(v)}/linhas/${id(linha)}/remocao`, {}),
  sugestao: (v: string, meses = 6) => pedir<Sugestao>(`/orcamento/versoes/${id(v)}/sugestao-do-historico?meses=${String(meses)}`),
  aplicarSugestao: (v: string, chaves: readonly string[], meses = 6) =>
    comCorpo<{ criadas: number; mesesNoHistorico: number; totalNoExercicio: number; exercicio: number }>('POST', `/orcamento/versoes/${id(v)}/sugestao-do-historico`, { chaves, meses }),
  gravarPremissa: (v: string, codigo: CodigoDaPremissa, valor: number, fonte: string, origem: 'historico' | 'informada' = 'informada') =>
    pedir<{ codigo: CodigoDaPremissa; valor: number; fonte: string; antes: number | null; versao: number }>(`/orcamento/versoes/${id(v)}/premissas`, {
      method: 'PUT',
      body: JSON.stringify({ codigo, valor, fonte: fonte.trim(), origem }),
    }),
  previstoRealizado: (competencia: string, versao?: string) =>
    pedir<PrevistoRealizado>(`/orcamento/previsto-realizado?competencia=${competencia.slice(0, 7)}${versao ? `&versao=${id(versao)}` : ''}`),
  explicar: (e: { versaoId: string; competencia: string; centroCustoId: string; contaId: string; texto: string }) =>
    comCorpo<{ id: string; competencia: string; centro: string | null; conta: string | null; nomeDaConta: string | null; texto: string; autor: string | null }>('POST', '/orcamento/desvios/explicacao', {
      ...e,
      competencia: e.competencia.slice(0, 7),
      texto: e.texto.trim(),
    }),
  limiares: () => pedir<{ pct: number; valor: number; padrao: boolean }>('/orcamento/parametros'),
  definirLimiares: (limiarPct: number, limiarValor: number) =>
    pedir<{ pct: number; valor: number; antes: { pct: number; valor: number } }>('/orcamento/parametros', { method: 'PUT', body: JSON.stringify({ limiarPct, limiarValor }) }),
  cenarios: (v: string) => pedir<Cenarios>(`/orcamento/versoes/${id(v)}/cenarios`),
  simulacao: (c: string) => pedir<Simulacao>(`/orcamento/cenarios/${id(c)}/simulacao`),
  criarCenario: (v: string, c: VariaveisDoCenario) =>
    comCorpo<{ id: string } & VariaveisDoCenario>('POST', `/orcamento/versoes/${id(v)}/cenarios`, { ...c, nome: c.nome.trim(), observacao: c.observacao?.trim() ?? '' }),
  /* `versao`: a de quando o formulario abriu (robos de 20/10/2026) — o
     servidor recusa o formulario velho em vez de gravar por cima. */
  alterarCenario: (c: string, v: VariaveisDoCenario, versao: string | null = null) =>
    pedir<{ id: string; antes: VariaveisDoCenario } & VariaveisDoCenario>(`/orcamento/cenarios/${id(c)}`, {
      method: 'PUT',
      body: JSON.stringify({ ...v, nome: v.nome.trim(), observacao: v.observacao?.trim() ?? '', ...(versao ? { versao } : {}) }),
    }),
  removerCenario: (c: string) => comCorpo<{ id: string; nome: string }>('POST', `/orcamento/cenarios/${id(c)}/remocao`, {}),
  gravarHipotese: (c: string, h: { tipo: string; contratoId: string | null; horasMes: number | null; fonte: string }) =>
    pedir<{ cenario: string; tipo: string; contrato: string | null; horasMes: number | null; antes: number | null }>(`/orcamento/cenarios/${id(c)}/hipoteses`, {
      method: 'PUT',
      body: JSON.stringify({ ...h, fonte: h.fonte.trim() }),
    }),
  caixa: (horizonte = 6) => pedir<Caixa>(`/orcamento/caixa?horizonte=${String(horizonte)}`),
  informarSaldo: (data: string, valor: number, fonte: string) =>
    comCorpo<{ id: string; data: string; valor: number; fonte: string; antes: { data: string; valor: number } | null }>('POST', '/orcamento/caixa/saldo', { data, valor, fonte: fonte.trim() }),
};
